import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" ;

  // Fetch projects and categories data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching projects data...');
        
        // Fetch projects and categories
        const [projectsResponse, categoriesResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/projects`),
          fetch(`${API_BASE_URL}/api/projects/categories`)
        ]);

        if (!projectsResponse.ok || !categoriesResponse.ok) {
          throw new Error(`API error: ${projectsResponse.status} or ${categoriesResponse.status}`);
        }

        const projectsData = await projectsResponse.json();
        const categoriesData = await categoriesResponse.json();

        console.log('✅ Projects data fetched:', { projectsData, categoriesData });

        if (projectsData.success && projectsData.data) {
          setProjects(projectsData.data);
        } else {
          setProjects([]);
        }

        if (categoriesData.success && categoriesData.data) {
          // Build categories with counts
          const projectCategories = projectsData.data || [];
          const categoriesWithCounts = categoriesData.data.map(cat => ({
            id: cat.slug || cat.id,
            name: cat.name,
            slug: cat.slug,
            count: projectCategories.filter(proj => proj.category === (cat.slug || cat.id)).length
          }));

          setCategories([
            { id: 'all', name: 'Tất cả dự án', count: projectCategories.length },
            ...categoriesWithCounts
          ]);
        } else {
          setCategories([]);
        }
        
      } catch (err) {
        console.error('❌ Error fetching projects:', err);
        setError(err.message);
        
        // Use fallback data
        setProjects(getFallbackProjects());
        setCategories(getFallbackCategories());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Filter and sort projects
  const filteredProjects = projects.filter(project => {
    const matchesCategory = selectedCategory === 'all' || project.category === selectedCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (project.excerpt && project.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt || b.updatedAt || b.end_date) - new Date(a.createdAt || a.updatedAt || a.end_date);
      case 'oldest':
        return new Date(a.createdAt || a.updatedAt || a.end_date) - new Date(b.createdAt || b.updatedAt || b.end_date);
      case 'name':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  // Get image URL
  const getImageUrl = (image) => {
    if (!image) return '/images/placeholder-project.svg';
    
    if (image.startsWith('/uploads/')) {
      return `${API_BASE_URL}${image}`;
    }
    
    return image;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get category display name
  const getCategoryName = (categoryKey) => {
    const category = categories.find(cat => cat.id === categoryKey || cat.slug === categoryKey);
    if (category) return category.name;
    
    // Fallback mapping
    const categoryMap = {
      'iot': 'IoT & Smart Home',
      'architecture': 'Kiến trúc & Xây dựng',
      'energy': 'Năng lượng mặt trời',
      'garden': 'Sân vườn & Cảnh quan',
      'automation': 'Tự động hóa',
      'security': 'An ninh & Bảo mật'
    };
    return categoryMap[categoryKey] || categoryKey;
  };

  // Create slug for project link
  const createSlug = (title, id) => {
    if (!title) return id;
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '-') + `-${id}`;
  };

  // Get project details from database fields
  const getProjectLocation = (project) => {
    return project.location || project.client || 'Chưa cập nhật';
  };

  const getProjectArea = (project) => {
    // Could be in description or a separate field
    return project.area || 'Chưa cập nhật';
  };

  const getProjectCompletionDate = (project) => {
    return project.completionDate || project.end_date || project.updatedAt;
  };

  const getProjectStatus = (project) => {
    return project.status || 'active';
  };

  // Fallback data
  const getFallbackProjects = () => [
    {
      id: 1,
      title: 'Smart Home Villa Thảo Điền',
      description: 'Hệ thống nhà thông minh cao cấp với đầy đủ tính năng IoT, điều khiển ánh sáng, nhiệt độ, an ninh tự động.',
      image: '/images/project-smart-home-1.jpg',
      category: 'iot',
      location: 'Quận 2, TP.HCM',
      area: '500m²',
      completedAt: '2024-11-15',
      featured: true,
      status: 'completed'
    },
    {
      id: 2,
      title: 'Hệ thống CCTV AI Vincom Center',
      description: 'Triển khai hệ thống camera giám sát thông minh với AI nhận diện khuôn mặt và phân tích hành vi.',
      image: '/images/project-cctv-ai-1.jpg',
      category: 'security',
      location: 'Quận 1, TP.HCM',
      area: '2000m²',
      completedAt: '2024-10-20',
      featured: false,
      status: 'completed'
    },
    {
      id: 3,
      title: 'Điện mặt trời nhà máy Samsung',
      description: 'Lắp đặt hệ thống năng lượng mặt trời công suất 500kW cho nhà máy sản xuất điện tử.',
      image: '/images/project-solar-factory.jpg',
      category: 'energy',
      location: 'Bắc Ninh',
      area: '3000m²',
      completedAt: '2024-09-30',
      featured: true,
      status: 'completed'
    },
    {
      id: 4,
      title: 'Thiết kế sân vườn Eco Park',
      description: 'Tạo cảnh quan xanh bền vững với hệ thống tưới tự động và ánh sáng thông minh.',
      image: '/images/project-garden-eco.jpg',
      category: 'garden',
      location: 'Hưng Yên',
      area: '1500m²',
      completedAt: '2024-08-15',
      featured: false,
      status: 'completed'
    },
    {
      id: 5,
      title: 'Tự động hóa nhà kho Vinhomes',
      description: 'Hệ thống quản lý kho thông minh với robot và IoT sensors.',
      image: '/images/project-automation-warehouse.jpg',
      category: 'automation',
      location: 'Hà Nội',
      area: '5000m²',
      completedAt: '2024-07-10',
      featured: false,
      status: 'in_progress'
    },
    {
      id: 6,
      title: 'Kiến trúc xanh Landmark 81',
      description: 'Tư vấn và thiết kế hệ thống building management cho tòa nhà cao ốc.',
      image: '/images/project-green-building.jpg',
      category: 'architecture',
      location: 'Quận Bình Thạnh, TP.HCM',
      area: '100000m²',
      completedAt: '2024-06-25',
      featured: true,
      status: 'completed'
    }
  ];

  const getFallbackCategories = () => [
    { id: 'all', name: 'Tất cả dự án', count: 6 },
    { id: 'iot', name: 'IoT & Smart Home', count: 1 },
    { id: 'security', name: 'An ninh & Bảo mật', count: 1 },
    { id: 'energy', name: 'Năng lượng mặt trời', count: 1 },
    { id: 'garden', name: 'Sân vườn & Cảnh quan', count: 1 },
    { id: 'automation', name: 'Tự động hóa', count: 1 },
    { id: 'architecture', name: 'Kiến trúc & Xây dựng', count: 1 }
  ];

  // Loading component
  const ProjectCardSkeleton = () => (
    <div className="project-card skeleton">
      <div className="project-image-skeleton"></div>
      <div className="project-content-skeleton">
        <div className="skeleton-line"></div>
        <div className="skeleton-line short"></div>
        <div className="skeleton-line"></div>
      </div>
    </div>
  );

  return (
    <main className="projects-main">
      {/* Hero Banner */}
      <div className="projects-hero">
        <img src="/images/banner1.jpg" alt="Projects Banner" className="hero-image" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>DỰ ÁN CỦA CHÚNG TÔI</h1>
          <p>Khám phá những dự án đã thực hiện thành công với công nghệ tiên tiến và giải pháp sáng tạo</p>
        </div>
      </div>

      <div className="projects-container">
        {/* Filter Section */}
        <div className="projects-filters">
          <div className="filters-row">
            {/* Search */}
            <div className="search-box">
              <input
                type="text"
                placeholder="Tìm kiếm dự án..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            {/* Sort */}
            <div className="sort-box">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="newest">Mới nhất</option>
                <option value="oldest">Cũ nhất</option>
                <option value="name">Tên A-Z</option>
              </select>
            </div>
          </div>

          {/* Categories */}
          <div className="categories-row">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
                <span className="count">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>⚠️ {error}</p>
            <p>Đang hiển thị dữ liệu mẫu...</p>
          </div>
        )}

        {/* Projects Grid */}
        <div className="projects-grid">
          {loading ? (
            // Loading skeletons
            [...Array(6)].map((_, index) => (
              <ProjectCardSkeleton key={index} />
            ))
          ) : filteredProjects.length > 0 ? (
            // Projects cards
            filteredProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-image">
                  <img 
                    src={getImageUrl(project.image)} 
                    alt={project.title}
                    onError={(e) => {
                      e.target.src = '/images/placeholder-project.svg';
                    }}
                  />
                  <div className="project-overlay">
                    <div className="project-meta">
                      <div className="project-badges">
                        <span className="category-badge">{project.category}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">
                    {project.excerpt || project.description || 'Chưa có mô tả'}
                  </p>
                  
                  <div className="project-details">
                    <div className="detail-item">
                      <span className="detail-label">📍 Địa điểm:</span>
                      <span className="detail-value">{getProjectLocation(project)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">📏 Diện tích:</span>
                      <span className="detail-value">{getProjectArea(project)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">📅 Hoàn thành:</span>
                      <span className="detail-value">{formatDate(getProjectCompletionDate(project))}</span>
                    </div>
                  </div>

                  <div className="project-actions">
                    <Link 
                      to={`/projects/${createSlug(project.title, project.id)}`} 
                      className="project-btn primary"
                    >
                      Xem chi tiết
                    </Link>
                    <button className="project-btn secondary">
                      Liên hệ tư vấn
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // No projects found
            <div className="no-projects">
              <div className="no-projects-content">
                <h3>🔍 Không tìm thấy dự án nào</h3>
                <p>Hãy thử thay đổi từ khóa tìm kiếm hoặc danh mục</p>
                <button 
                  className="reset-filters-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                >
                  Reset bộ lọc
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Projects Stats */}
        {filteredProjects.length > 0 && (
          <div className="projects-stats">
            <p>
              Hiển thị <strong>{filteredProjects.length}</strong> dự án 
              {selectedCategory !== 'all' && ` trong danh mục "${categories.find(c => c.id === selectedCategory)?.name}"`}
              {searchTerm && ` với từ khóa "${searchTerm}"`}
            </p>
          </div>
        )}
      </div>
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </main>
  );
};

export default Projects; 